import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Certification {
  id: number;
  name: string;
  issuer: string;
  validUntil: string;
  category: string;
  image: string;
  alt: string;
}

interface CertificationsComplianceProps {
  certifications: Certification[];
}

export default function CertificationsCompliance({ certifications }: CertificationsComplianceProps) {
  const categories = Array.from(new Set(certifications.map(cert => cert.category)));

  return (
    <section className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-primary mb-4">
            Certifications & Compliance
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            Industry-recognized certifications and compliance standards ensuring quality and safety in every project
          </p>
        </div>

        {categories.map((category) => (
          <div key={category} className="mb-12 last:mb-0">
            <h3 className="text-2xl font-headline font-bold text-textPrimary mb-6 flex items-center">
              <Icon name="ShieldCheckIcon" size={28} className="text-success mr-3" />
              {category}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {certifications
                .filter(cert => cert.category === category)
                .map((cert) => (
                  <div
                    key={cert.id}
                    className="bg-card rounded-lg shadow-subtle overflow-hidden hover:shadow-modal transition-shadow duration-300"
                  >
                    <div className="relative h-40 bg-surface flex items-center justify-center p-4">
                      <AppImage
                        src={cert.image}
                        alt={cert.alt}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>

                    <div className="p-4">
                      <h4 className="text-sm font-body font-bold text-textPrimary mb-2 line-clamp-2">
                        {cert.name}
                      </h4>
                      <p className="text-xs text-textSecondary mb-2">{cert.issuer}</p>
                      <div className="flex items-center text-xs text-success">
                        <Icon name="CalendarIcon" size={14} className="mr-1" />
                        Valid until {cert.validUntil}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}